import * as React from 'react';
import { Link } from 'react-router-dom';

import EditIcon from '@mui/icons-material/Edit';
import { Avatar, Box, Stack, Typography } from '@mui/material';

import { type MRT_ColumnDef } from 'material-react-table';
import { capitalize } from 'lodash';

import Button from '../../Button/Button';
import { FeedIcon, ReelIcon, StoryIcon, TikTokIcon } from '../../Icons';
import { DaysOfWeek, Offer, OfferCategory } from '../../../graphql/API';
import StatusLabel from '../../StatusLabel/StatusLabel';
import icons from '../../../themes/icons';
import { datetimeWithTimezone } from '../../../utils/dateV2';

const dayOrder = Object.values(DaysOfWeek);

const requirementBadges = [
  { key: 'postOnInstagramFeed', Icon: FeedIcon, label: 'Feed' },
  { key: 'postOnInstagramReels', Icon: ReelIcon, label: 'Reels' },
  { key: 'postOnInstagramStories', Icon: StoryIcon, label: '1 story' },
  { key: 'postOnInstagramStories2x', Icon: StoryIcon, label: '2 stories' },
  { key: 'postOnInstagramStories3x', Icon: StoryIcon, label: '3 stories' },
  { key: 'postOnTikTokVideo', Icon: TikTokIcon, label: 'TikTok' },
] as const;

const getScheduleLabel = (data: Offer) => {
  const isEvent = data.category === OfferCategory.event;
  const timezoneId =
    isEvent && data.offerTimeZoneId ? data.offerTimeZoneId : data.timeZoneId;

  const startDatetime = datetimeWithTimezone({
    date: data.startDate,
    time: data.startTime,
    timezoneId,
  }).tz();
  const endDatetime = datetimeWithTimezone({
    date: data.startDate,
    time: data.endTime,
    timezoneId,
  }).tz();

  const sortedDays = [...(data.availableDays || [])].sort(
    (a, b) => dayOrder.indexOf(a as DaysOfWeek) - dayOrder.indexOf(b as DaysOfWeek),
  );
  const isEveryday = sortedDays.length === dayOrder.length;
  const daysLabel = isEveryday
    ? 'Everyday'
    : `Every ${sortedDays.map((day) => capitalize(day as string)).join(', ')}`;

  const dateLabel = isEvent ? startDatetime.format('ll') : daysLabel;
  const timeLabel = `${startDatetime.format('LT')} - ${endDatetime.format('LT')}`;

  return { dateLabel, timeLabel };
};

const Badge: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      px: 1,
      py: 0.25,
      borderRadius: '999px',
      backgroundColor: '#F3F3F3',
    }}
  >
    {children}
  </Box>
);

export type OfferColumnVariant = 'offer' | 'product';

export const getOfferColumns = (
  variant: OfferColumnVariant = 'offer',
  requestCounts: Record<string, number> = {},
): MRT_ColumnDef<Offer>[] => {
  const isProduct = variant === 'product';
  const basePath = isProduct ? 'products' : 'offers';
  const detailsLabel = isProduct ? 'Product details' : 'Offer details';

  const titleColumn: MRT_ColumnDef<Offer> = {
    accessorKey: 'title',
    header: isProduct ? 'Product' : 'Offer',
    size: 260,
    enableSorting: true,
    Cell: ({ row }) => {
      const data = row.original;
      return (
        <Stack direction='row' alignItems='center' gap={1.5}>
          <Avatar
            variant='rounded'
            src={String(data.picture?.small)}
            sx={{ width: 48, height: 48, borderRadius: '10px', flexShrink: 0 }}
          />
          <Typography sx={{ fontSize: '15px', fontWeight: 500 }} noWrap>
            {data.title}
          </Typography>
        </Stack>
      );
    },
  };

  const scheduleColumn: MRT_ColumnDef<Offer> = {
    id: 'schedule',
    header: 'Schedule',
    size: 240,
    enableSorting: false,
    Cell: ({ row }) => {
      const data = row.original;
      if (data.private) return <span>—</span>;
      const { dateLabel, timeLabel } = getScheduleLabel(data);
      return (
        <Box>
          <Typography sx={{ fontSize: '14px' }} noWrap>
            {dateLabel}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: 'rgba(17, 17, 17, 0.50)' }} noWrap>
            {timeLabel}
          </Typography>
        </Box>
      );
    },
  };

  const platformColumn: MRT_ColumnDef<Offer> = {
    id: 'platform',
    header: 'Requirement',
    size: 150,
    enableSorting: true,
    sortingFn: 'basic',
    accessorFn: (row) => {
      if (row.private) return 0;
      const idx = requirementBadges.findIndex(
        ({ key }) => row[key as keyof Offer] === true,
      );
      return idx >= 0 ? idx + 1 : Number.MAX_SAFE_INTEGER;
    },
    Cell: ({ row }) => {
      const data = row.original;
      if (data.private) {
        return (
          <Badge>
            <icons.PaidCollabV2Icon style={{ width: 14, height: 14 }} />
            <Typography sx={{ fontSize: 12, fontWeight: 500 }}>Collab</Typography>
          </Badge>
        );
      }
      const activeRequirements = requirementBadges.filter(
        ({ key }) => data[key as keyof Offer] === true,
      );
      if (activeRequirements.length === 0) return <span>—</span>;
      return (
        <Stack direction='row' alignItems='center' gap={0.5} flexWrap='wrap'>
          {activeRequirements.map(({ Icon, label }, i) => (
            <Badge key={`req-${i}`}>
              <Icon sx={{ fontSize: 14 }} />
              <Typography sx={{ fontSize: 12, fontWeight: 500 }}>{label}</Typography>
            </Badge>
          ))}
        </Stack>
      );
    },
  };

  const productRequestColumn: MRT_ColumnDef<Offer> = {
    id: 'productRequest',
    header: 'Prod. requests',
    size: 140,
    enableSorting: true,
    sortingFn: 'basic',
    accessorFn: (row) => requestCounts[row.id] ?? 0,
    Cell: ({ row }) => <span>{requestCounts[row.original.id] ?? 0}</span>,
  };

  const reservationsColumn: MRT_ColumnDef<Offer> = {
    accessorKey: 'totalReservations',
    header: 'Reservations',
    size: 130,
    enableSorting: true,
    sortingFn: 'basic',
    Cell: ({ row }) => <span>{row.original.totalReservations ?? 0}</span>,
  };

  const checkinsColumn: MRT_ColumnDef<Offer> = {
    accessorKey: 'totalCheckins',
    header: 'Check-ins',
    size: 120,
    enableSorting: true,
    sortingFn: 'basic',
    Cell: ({ row }) => <span>{row.original.totalCheckins ?? 0}</span>,
  };

  const viewsColumn: MRT_ColumnDef<Offer> = {
    accessorKey: 'totalUniqueVisitors',
    header: 'Views',
    size: 110,
    enableSorting: true,
    sortingFn: 'basic',
    Cell: ({ row }) => <span>{row.original.totalUniqueVisitors ?? 0}</span>,
  };

  const statusColumn: MRT_ColumnDef<Offer> = {
    accessorKey: 'status',
    header: 'Status',
    size: 130,
    enableSorting: true,
    Cell: ({ row }) => (
      <StatusLabel type='offer' status={row.original.status} fontSize='13px' />
    ),
  };

  const actionsColumn: MRT_ColumnDef<Offer> = {
    id: 'actions',
    header: '',
    size: 200,
    enableSorting: false,
    Cell: ({ row }) => {
      const data = row.original;
      const isPrivate = data.private;
      const isEvent = data.category === OfferCategory.event;
      const showInviteButton = isPrivate || isEvent;
      const showDetailsButton = !isPrivate || isEvent;

      return (
        <Stack direction='row' alignItems='center' gap={1} justifyContent='flex-end'>
          <Button
            component={Link}
            to={`/${basePath}/${data.id}/edit`}
            aria-label='Edit'
            variant='contained'
            color='secondary'
            sx={{ padding: '4px 10px', minWidth: 40, width: 40, height: 40 }}
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
              e.stopPropagation()
            }
          >
            <EditIcon fontSize='small' />
          </Button>

          {showDetailsButton && (
            <Button
              component={Link}
              variant='contained'
              color='secondary'
              to={`/${basePath}/${data.id}`}
              sx={{ whiteSpace: 'nowrap', color: '#000', '&:hover': { color: '#000' } }}
              onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                e.stopPropagation()
              }
            >
              {isEvent ? 'Details' : detailsLabel}
            </Button>
          )}

          {showInviteButton && (
            <Button
              component={Link}
              variant={showDetailsButton ? 'contained' : 'outlined'}
              to={`/collabs?collabId=${data.id}`}
              sx={{
                whiteSpace: 'nowrap',
                ...(showDetailsButton
                  ? {
                      backgroundColor: 'black',
                      color: 'white',
                      '&:hover': { backgroundColor: '#333' },
                    }
                  : { borderWidth: 2, borderColor: 'black' }),
              }}
              onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                e.stopPropagation()
              }
            >
              Invite
            </Button>
          )}
        </Stack>
      );
    },
  };

  if (isProduct) {
    // Online brands have no check-ins; show product request counter instead.
    return [
      titleColumn,
      platformColumn,
      productRequestColumn,
      reservationsColumn,
      viewsColumn,
      statusColumn,
      actionsColumn,
    ];
  }

  return [
    titleColumn,
    scheduleColumn,
    platformColumn,
    reservationsColumn,
    checkinsColumn,
    viewsColumn,
    statusColumn,
    actionsColumn,
  ];
};
